import { MONTHS, type PeriodValue } from '@/components/PeriodModal';
import type { TasksListFilter } from '@/store/tasks/tasks.queries';

/**
 * The Maintain lists' year/month filter, shared by the schedule and the service history
 * so the two read, write and show a period the same way.
 *
 * It filters on the deadline (the time slot's end) — the field the schedule's Due column
 * shows and sorts by — through activity's `due_from`/`due_to`, which hold independently
 * of the `from`/`to` pair the schedule's due buckets already spend.
 */
export type MaintainPeriod = {
  /** e.g. `2026`; empty means any — no period at all. */
  year: string;
  /** `1`–`12` within `year`; empty means the whole year. */
  month: string;
};

/** `?year=2026[&month=3]` narrows a Maintain list to jobs due in that year or month. */
export const YEAR_PARAM = 'year';
export const MONTH_PARAM = 'month';

export const NO_PERIOD: MaintainPeriod = { year: '', month: '' };

/** A month means nothing without its year, so dropping the year drops both. */
export const makePeriod = (year?: string, month?: string): MaintainPeriod => ({
  year: year ?? '',
  month: year ? (month ?? '') : '',
});

export const hasPeriod = (period: MaintainPeriod) => !!period.year;

/** "Oct 2026", "2026" — the label the period button and the chip both show. */
export const periodLabel = ({ year, month }: MaintainPeriod) => {
  if (!year) return '';
  if (!month) return year;
  return `${MONTHS[Number(month) - 1].slice(0, 3)} ${year}`;
};

/**
 * The period as a half-open deadline range, in the viewer's local time: a job due at
 * 23:30 on the last of the month belongs to that month for the person reading the list.
 */
export const periodRange = ({
  year,
  month,
}: MaintainPeriod): Pick<TasksListFilter, 'due_from' | 'due_to'> | undefined => {
  if (!year) return undefined;
  const y = Number(year);
  const from = month ? new Date(y, Number(month) - 1, 1) : new Date(y, 0, 1);
  const to = month ? new Date(y, Number(month), 1) : new Date(y + 1, 0, 1);
  return { due_from: from.toISOString(), due_to: to.toISOString() };
};

export const readPeriodParams = (params: URLSearchParams): MaintainPeriod => {
  const year = params.get(YEAR_PARAM) ?? '';
  const month = Number(params.get(MONTH_PARAM));
  return makePeriod(
    /^\d{4}$/.test(year) ? year : undefined,
    Number.isInteger(month) && month >= 1 && month <= 12 ? String(month) : undefined,
  );
};

/** Writes the period into `params` in place; no period leaves no param behind. */
export const writePeriodParams = (params: URLSearchParams, { year, month }: MaintainPeriod) => {
  if (year) params.set(YEAR_PARAM, year);
  else params.delete(YEAR_PARAM);
  if (year && month) params.set(MONTH_PARAM, month);
  else params.delete(MONTH_PARAM);
};

/**
 * How far back and ahead the picker's Year wheel reaches from the current year. Back,
 * unlike the budget's: Maintain is read for the work already done as much as for what
 * is coming.
 */
const YEARS_BACK = 4;
const YEARS_AHEAD = 1;

/** Oldest first, as a wheel reads; an arriving link's year is kept even outside the window. */
export const periodYears = (selected?: string) => {
  const current = new Date().getFullYear();
  const years = Array.from(
    { length: YEARS_BACK + YEARS_AHEAD + 1 },
    (_, index) => current - YEARS_BACK + index,
  ).map(String);
  if (selected && !years.includes(selected)) years.push(selected);
  return years.sort((a, b) => Number(a) - Number(b));
};

/**
 * The period as `PeriodModal` reads it. With none set the wheels open on the current
 * month — the picker always holds a value, so "any period" is the absence of the filter
 * rather than a position on it.
 */
export const toPeriodValue = ({ year, month }: MaintainPeriod): PeriodValue => {
  const now = new Date();
  if (!year) {
    return { period: 'monthly', month: MONTHS[now.getMonth()], year: String(now.getFullYear()) };
  }
  return month
    ? { period: 'monthly', month: MONTHS[Number(month) - 1], year }
    : { period: 'yearly', month: MONTHS[now.getMonth()], year };
};

/** Back from the picker: a month is kept only while it is the month view. */
export const fromPeriodValue = (value: PeriodValue): MaintainPeriod =>
  makePeriod(
    value.year,
    value.period === 'monthly' ? String(MONTHS.indexOf(value.month) + 1) : undefined,
  );
